/**
 * Every field of Majik's clients, locations, contacts and cleaners
 * (docs/majik/DATA-MODEL-V1.md §2.2–§2.4; labels from DATA-ARCHITECTURE-SPEC).
 * The keys are the app's own; `main-records.test.ts` fails until every column
 * of each table is described here.
 */
import type { FieldDef } from '../types.ts'
import { fieldsOf, standardFields } from './define.ts'

export const contactFieldDefs: readonly FieldDef[] = [
  ...standardFields('contact', 'contact'),
  ...fieldsOf('contact', [
    [
      'firstName',
      'First name',
      'text',
      'personal',
      'Their first name. A billing inbox with no person’s name holds the address itself — a name is never invented.',
    ],
    ['lastName', 'Last name', 'text', 'personal', 'Their last name. Can be empty.'],
    [
      'email',
      'Email',
      'text',
      'personal',
      'Their main current email, stored lowercase. Kept with their other addresses, one row each; empty when only a phone is known.',
    ],
    [
      'phone',
      'Phone',
      'text',
      'personal',
      'Their main current mobile, as +1 and ten digits. Kept with their other numbers, one row each; empty when only an email is known.',
    ],
    [
      'otherMethods',
      'Other phones and emails',
      'text',
      'personal',
      'Numbers and addresses besides the main ones, current or old. An old one is kept so a search still finds them, and is never texted or emailed.',
    ],
    [
      'language',
      'Language',
      'code',
      'general',
      'The language their messages are written in. English unless someone picks another.',
    ],
    [
      'textsBlocked',
      'Texts blocked',
      'flag',
      'general',
      'They texted STOP, or the carrier said they unsubscribed. Never text them while this is on.',
    ],
    [
      'textsBlockedAt',
      'Texts blocked at',
      'datetime',
      'general',
      'When texts were blocked. Empty while they can be texted.',
    ],
    [
      'isInspector',
      'Inspector',
      'flag',
      'general',
      'Can be picked on an inspection, which is sent to them by text.',
    ],
    [
      'isStaff',
      'Staff',
      'flag',
      'general',
      'Works in Majik’s office. Kept for staff broadcasts; a staff member is not a cleaner.',
    ],
    [
      'notes',
      'Notes',
      'text',
      'personal',
      'Anything worth keeping about the person. Internal; never sent.',
    ],
  ]),
]

export const clientFieldDefs: readonly FieldDef[] = [
  ...standardFields('client', 'client'),
  ...fieldsOf('client', [
    [
      'name',
      'Account name',
      'text',
      'personal',
      'The customer’s name as it appears on invoices. Often a business, sometimes a person.',
    ],
    [
      'taxCode',
      'Tax',
      'code',
      'general',
      'Whether sales tax is added to this client’s invoices. Copied to each new invoice.',
    ],
    [
      'paymentTerms',
      'Payment terms',
      'code',
      'general',
      'How long the client has to pay. Copied to each new invoice.',
    ],
    [
      'billingMethod',
      'Billing method',
      'code',
      'general',
      'How the client is billed: a flat monthly rate, visits rolled into a monthly invoice, or each visit invoiced on its own.',
    ],
    [
      'quickbooksCustomerId',
      'QuickBooks ID',
      'text',
      'operations',
      'The customer’s id in QuickBooks. Empty until the client is connected; never shown on the Clients list.',
    ],
    [
      'billingAddress',
      'Billing address',
      'text',
      'personal',
      'Where invoices are mailed when it differs from the locations, as free text — the way Monday had it. Empty when not given.',
    ],
    ['notes', 'Notes', 'text', 'personal', 'Internal account notes. The client never sees them.'],
    [
      'paymentRemindersPaused',
      'Payment reminders paused',
      'flag',
      'general',
      'No payment reminders go out on this client’s invoices while this is on.',
    ],
  ]),
]

export const locationFieldDefs: readonly FieldDef[] = [
  ...standardFields('location', 'location'),
  ...fieldsOf('location', [
    [
      'clientId',
      'Client',
      'id',
      'general',
      'The record number of the client this site belongs to.',
    ],
    [
      'name',
      'Location name',
      'text',
      'personal',
      'What the site is called, e.g. “Main office – 5th floor”. Two locations may share a name.',
    ],
    [
      'status',
      'Status',
      'code',
      'general',
      'Whether the site is being cleaned. A client’s status is the best of its locations’.',
    ],
    [
      'addressLine1',
      'Address',
      'text',
      'personal',
      'The street address. An imported site Monday gave no address holds its name instead, and is listed for review.',
    ],
    ['addressLine2', 'Address 2', 'text', 'personal', 'Floor, suite or apartment. Can be empty.'],
    [
      'city',
      'City',
      'text',
      'personal',
      'Can be empty on an imported location — Monday often has only a street. The form still asks for it.',
    ],
    [
      'state',
      'State',
      'text',
      'personal',
      'Two letters, e.g. NY. Can be empty on an imported location.',
    ],
    [
      'postalCode',
      'ZIP',
      'text',
      'personal',
      'Five digits, kept as text so leading zeros stay. Can be empty on an imported location.',
    ],
    [
      'workNotes',
      'Work notes',
      'text',
      'personal',
      'Printed on every assigned cleaner’s schedule.',
    ],
    [
      'accessNotes',
      'Access notes',
      'text',
      'personal',
      'Keys, alarm codes, the entrance. Kept apart from the work notes so codes are never on a schedule.',
    ],
    [
      'serviceFrequency',
      'Frequency',
      'code',
      'general',
      'How often the site is cleaned, as Monday recorded it. The real schedule is its recurring jobs.',
    ],
    [
      'nextBillDate',
      'Next bill',
      'date',
      'general',
      'The day the next monthly invoice is due to be built, on New York’s calendar.',
    ],
    [
      'billingCadence',
      'Billing cadence',
      'code',
      'general',
      'How far Next bill moves after each monthly invoice.',
    ],
    [
      'remindersEnabled',
      'Send reminders',
      'flag',
      'general',
      'May get the “it has been a while” email.',
    ],
    [
      'lastReminderOn',
      'Last reminder',
      'date',
      'general',
      'The day the last “it has been a while” email went out. Empty when none has.',
    ],
  ]),
]

export const cleanerFieldDefs: readonly FieldDef[] = [
  ...standardFields('cleaner', 'cleaner'),
  ...fieldsOf('cleaner', [
    [
      'contactId',
      'Contact',
      'id',
      'general',
      'The record number of the contact who is this cleaner — their name, phone and email are there.',
    ],
    ['status', 'Status', 'code', 'general', 'Whether they are working for Majik now.'],
    [
      'shift',
      'Day type',
      'code',
      'general',
      'Whether they work days or nights. Empty only on an imported cleaner Monday gave none; the form still asks.',
    ],
    [
      'notifyByText',
      'Notify by text',
      'flag',
      'general',
      'Gets the nightly job list and schedules by text as well as email.',
    ],
  ]),
]
